import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Modal } from "react-bootstrap";
import { BsThreeDots } from "react-icons/bs";
import { useDeleteNotification } from "../../api/mutations/useDeleteNotification";
import { useSaveNotification } from "../../api/mutations/useSaveNotification";
import { useFindUsers } from "../../api/queries/useFindUser";
import { useGetUserNotification } from "../../api/queries/useGetUserNotification";
import { Notification } from "../../types/Types";
import "../css/Notifications.css";

const DESC_MAX_WIDTH_STYLE: React.CSSProperties = { maxWidth: 300 };
const LIST_MIN_HEIGHT = 240;
const LIST_STYLE: React.CSSProperties = { minHeight: LIST_MIN_HEIGHT };
const NOTIFICATION_AVATAR_SIZE = 48;

function NotificationsInner() {
  const [showDropdown, setShowDropdown] = useState<number | null>(null);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: userResponse } = useFindUsers({ pageSize: 10, page: 1 });
  const objectId = localStorage.getItem("objectId");
  const userId = userResponse?.users?.data?.find(
    (item: { objectId: string | null }) => item.objectId === objectId
  )?.id;

  const { data: notifications, refetch } = useGetUserNotification({
    id: userId ?? 0,
  });

  const mergedNotifications = useMemo(
    () =>
      (notifications?.data ?? []).map(
        (notification: { id: number; userId: number }) => {
          const imageEntry = userResponse?.userImageUrls?.find(
            (img: { userId: number }) => img.userId === notification.userId
          );
          return {
            ...notification,
            ...imageEntry?.imageUrls,
          };
        }
      ),
    [notifications?.data, userResponse]
  );

  const sortedNotifications = useMemo(
    () =>
      [...mergedNotifications].sort(
        (a: Notification, b: Notification) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
      ),
    [mergedNotifications]
  );

  const { mutate: updateNotification } = useSaveNotification({
    onSuccess: () => refetch(),
  });

  const { mutate: deleteNotification } = useDeleteNotification({
    onSuccess: () => {
      refetch();
      setShowConfirmModal(false);
      setConfirmDeleteId(null);
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (confirmDeleteId !== null) {
      deleteNotification(confirmDeleteId);
    }
  }, [confirmDeleteId, deleteNotification]);

  const toggleDropdown = useCallback((id: number) => {
    setShowDropdown((prev) => (prev === id ? null : id));
  }, []);

  const handleMarkAsRead = useCallback(
    (notification: Notification) => {
      updateNotification({ ...notification, isRead: true });
      setShowDropdown(null);
    },
    [updateNotification]
  );

  const handleMarkAsUnread = useCallback(
    (notification: Notification) => {
      updateNotification({ ...notification, isRead: false });
      setShowDropdown(null);
    },
    [updateNotification]
  );

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      setSelectedNotification(notification);
      setShowModal(true);
      if (!notification.isRead) {
        updateNotification({ ...notification, isRead: true });
      }
    },
    [updateNotification]
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedNotification(null);
  }, []);

  const handleDeleteClick = useCallback((id: number) => {
    setConfirmDeleteId(id);
    setShowConfirmModal(true);
    setShowDropdown(null);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="notifications-main-container" style={{ overflowX: "hidden", position: "relative" }}>
      <style dangerouslySetInnerHTML={{
        __html: `
          .notifications-main-container {
            padding-top: 24px !important;
          }
          .notifications-header {
            padding-top: 24px !important;
          }
          @media (max-width: 575.98px) {
            .notifications-main-container {
              padding-top: 24px !important;
            }
            .notifications-header {
              padding-top: 24px !important;
            }
          }
        `
      }} />
      <div className="container py-3 notifications-header" style={{ paddingTop: "24px" }}>
        <h3 className="mb-4">Notifications</h3>
      <div className="list-group" style={LIST_STYLE}>
        {notifications?.data.length === 0 ? (
          <div className="text-center text-muted py-5">
            <h5>No notifications found</h5>
          </div>
        ) : (
          sortedNotifications.map((item: Notification) => (
              <div
                key={item.id}
                className={`list-group-item list-group-item-action d-flex align-items-start justify-content-between ${
                  item.isRead ? "" : "bg-light-blue"
                } cursor-pointer`}
                onClick={() => handleNotificationClick(item)}
              >
                <div className="d-flex">
                  <img
                    src={item?.ProfessionalPhoto || item?.PersonalProfessionalPhoto || "/user.png"}
                    alt={item.name ? `Notification from ${item.name}` : "Notification"}
                    width={NOTIFICATION_AVATAR_SIZE}
                    height={NOTIFICATION_AVATAR_SIZE}
                    className="rounded-circle me-3 object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div>
                    <div className="fw-bold">{item.name}</div>
                    <div
                      className="text-sm text-muted text-truncate"
                      style={DESC_MAX_WIDTH_STYLE}
                    >
                      {item.description}
                    </div>
                  </div>
                </div>

                <div
                  className="text-end d-flex flex-column align-items-end justify-content-between ms-2 position-relative"
                  ref={showDropdown === item.id ? dropdownRef : null}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="notification-time text-muted">
                    {new Date(item.date).toLocaleString()}
                  </div>
                  <Button
                    variant="light"
                    onClick={() => toggleDropdown(item.id)}
                    className="border-0 p-0 text-center rounded-circle"
                  >
                    <BsThreeDots className="text-muted m-1 cursor-pointer" />
                  </Button>

                  {showDropdown === item.id && (
                    <div className="dropdown-menu show">
                      <button
                        className="dropdown-item"
                        onClick={() => handleMarkAsRead(item)}
                      >
                        Mark as Read
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => handleMarkAsUnread(item)}
                      >
                        Mark as Unread
                      </button>
                      <button
                        className="dropdown-item text-danger"
                        onClick={() => handleDeleteClick(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
        )}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>{selectedNotification?.name}</h5>
          <p>{selectedNotification?.description}</p>
          <div className="text-muted small">
            {selectedNotification?.date &&
              new Date(selectedNotification.date).toLocaleString()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmModal}
        onHide={handleCloseConfirmModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this notification?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseConfirmModal}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    </div>
  );
}

NotificationsInner.displayName = "NotificationsPage";

export default memo(NotificationsInner);
